import React, { useState, useEffect, useRef } from 'react';
import './TravelerCarousel.css';
import AdventureTraveler from 'url:../../assets/redesign/gifs/AdventureTraveler.mp4';
import InternationalTraveler from 'url:../../assets/redesign/gifs/InternationalTraveler.mp4';
import LiveEntertainmentTraveler from 'url:../../assets/redesign/gifs/LiveEntertainmentTraveler.mp4';
import SportTraveler from 'url:../../assets/redesign/gifs/SportTraveler.mp4';
import carouselData from './travelerCarouselData.json';

const videoAssets = {
  'AdventureTraveler.mp4': AdventureTraveler,
  'InternationalTraveler.mp4': InternationalTraveler,
  'LiveEntertainmentTraveler.mp4': LiveEntertainmentTraveler,
  'SportTraveler.mp4': SportTraveler,
};
import BgVideoContainer from './BgVideoContainer';
import MaskedVideoFrame from './MaskedVideoFrame';
import ActionButton from '../elements/ActionButton/ActionButton';

const ZOOM_OUT_DURATION = 400;
const PAUSE = 150;
const SLIDE_DURATION = 450;
const ANIMATION_DURATION = ZOOM_OUT_DURATION + PAUSE + SLIDE_DURATION + PAUSE + 400;

const TravelerCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomingOut, setZoomingOut] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isAnimating = useRef(false);
  const pendingIndex = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Trigger entrance animation after mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => setInitialized(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const triggerSlideChange = (index) => {
    if (index === activeIndex || isAnimating.current) return;
    isAnimating.current = true;
    pendingIndex.current = index;
    setZoomingOut(true);

    // After zoom-out + pause, commit the slide change
    setTimeout(() => {
      setZoomingOut(false);
      setActiveIndex(index);
      pendingIndex.current = null;

      // Unlock after the slide + pause + zoom-in finishes
      setTimeout(
        () => {
          isAnimating.current = false;
        },
        SLIDE_DURATION + PAUSE + 400
      );
    }, ZOOM_OUT_DURATION + PAUSE);
  };

  const handleSlideChange = (index) => {
    triggerSlideChange(index);
  };

  const activeSlide = carouselData[activeIndex];
  const activeVideoSrc = activeSlide ? videoAssets[activeSlide.videoAsset] : undefined;

  return (
    <section className='traveler-carousel-section'>
      {/* Shared blurred background */}
      <BgVideoContainer videoSrc={activeVideoSrc} />

      <div className='sr-only' aria-live='polite'>
        {`Slide ${activeIndex + 1} of ${carouselData.length}: ${activeSlide?.mainText || ''}`}
      </div>

      <div className='tc-slider'>
        <div className='tc-viewport' style={{ aspectRatio: isMobile ? '390/680' : '16/10' }}>
          {carouselData.map((slide, index) => (
            <div
              key={slide.id}
              id={`tc-panel-${slide.id}`}
              role='tabpanel'
              aria-labelledby={`tc-tab-${slide.id}`}
              aria-hidden={index !== activeIndex}
              className={`tc-slide ${index === activeIndex ? 'tc-slide--active' : ''} ${zoomingOut && index === activeIndex ? 'tc-slide--zooming-out' : ''}`}
            >
              <div className={`tc-slide__wrap ${initialized ? 'tc-slide__wrap--ready' : ''}`}>
                <MaskedVideoFrame
                  topText={slide.topText}
                  mainText={slide.mainText}
                  videoSrc={videoAssets[slide.videoAsset]}
                  maskId={`travel-mask-${slide.id}`}
                  mobileTallLayout={isMobile}
                  videoContainerClassName='tc-slide__video'
                  isActive={index === activeIndex}
                >
                  <div className='ui-overlay'>
                    <div className='learn-more-container'>
                      <ActionButton url={slide.url || '#'} text='Learn more' />
                    </div>
                    <div className='info-pill'>
                     <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_295_6633)">
                      <path d="M9 7.5C10.6569 7.5 12 6.15685 12 4.5C12 2.84315 10.6569 1.5 9 1.5C7.34315 1.5 6 2.84315 6 4.5C6 6.15685 7.34315 7.5 9 7.5Z" stroke="#171717" strokeWidth="1.5"/>
                      <path d="M15 13.125C15 14.9887 15 16.5 9 16.5C3 16.5 3 14.9887 3 13.125C3 11.2613 5.6865 9.75 9 9.75C12.3135 9.75 15 11.2613 15 13.125Z" stroke="#171717" strokeWidth="1.5"/>
                      </g>
                      <defs>
                      <clipPath id="clip0_295_6633">
                      <rect width="18" height="18" fill="white"/>
                      </clipPath>
                      </defs>
                    </svg>

                      <span>{slide.infoText}</span>
                    </div>
                  </div>
                </MaskedVideoFrame>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Carousel Pagination */}
      <div className='tc-controls-row'>
        <div className='carousel-dots' role='tablist' aria-label='Traveler stories'>
          {carouselData.map((slide, index) => (
            <button
              key={slide.id}
              type='button'
              role='tab'
              id={`tc-tab-${slide.id}`}
              aria-selected={index === activeIndex}
              aria-controls={`tc-panel-${slide.id}`}
              aria-label={slide.mainText || `Slide ${index + 1}`}
              className={`dot ${index === activeIndex ? 'active' : ''}`}
              onClick={() => handleSlideChange(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TravelerCarousel;
